import { useState, useEffect } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setActiveCourseData } from '@/lib/slices/course-slice';
import Request from '@/services/util/request-service';
import toast from 'react-hot-toast';
import { ActivityData, SubunitData, SubunitId, UnitId, CourseData } from 'course-types';

interface UseUnitContentManagerOptions {
  unitId: string;
  subunits: { [id: string]: SubunitData };
  activities: ActivityData[];
}

interface UseUnitContentManagerReturn {
  localSortedContents: Array<(SubunitData | ActivityData) & { id: string }>;
  localSubunitActivities: { [subunitId: string]: ActivityData[] };
  activeUnitContentDragId: string | null;
  handleDragEnd: (event: DragEndEvent) => Promise<void>;
  handleSubunitActivityDragEnd: (event: DragEndEvent, subunitId: string) => Promise<void>;
  handleDragStart: (event: any) => void;
  handleDragCancel: () => void;
}

/**
 * Custom hook to manage unit content (subunits and activities) state and drag-and-drop logic.
 * Handles local state, API calls for reordering, and Redux state updates.
 */
export function useUnitContentManager({
  unitId,
  subunits,
  activities,
}: UseUnitContentManagerOptions): UseUnitContentManagerReturn {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const activeCourse = useAppSelector(() => store.getState().course.activeCourseData);

  // Safeguard: ensure activities is an array
  const safeActivities: ActivityData[] = Array.isArray(activities) ? activities : [];

  // Local state for reordered content
  const [localSortedContents, setLocalSortedContents] = useState<
    Array<(SubunitData | ActivityData) & { id: string }>
  >([]);
  const [localSubunitActivities, setLocalSubunitActivities] = useState<{
    [subunitId: string]: ActivityData[];
  }>({});
  const [activeUnitContentDragId, setActiveUnitContentDragId] = useState<string | null>(null);

  // Initialize local state when props change
  useEffect(() => {
    const computedSorted: Array<(SubunitData | ActivityData) & { id: string }> = [
      ...Object.entries(subunits ?? {}).map(([key, value]) => ({ id: key, ...value })),
      ...safeActivities,
    ].sort((a, b) => (a.index ?? -1) - (b.index ?? -1));
    setLocalSortedContents(computedSorted);

    // Initialize subunit activities
    const subunitActivitiesMap: { [subunitId: string]: ActivityData[] } = {};
    Object.entries(subunits ?? {}).forEach(([subunitId, subunit]) => {
      subunitActivitiesMap[subunitId] = [...(subunit.activities ?? [])];
    });
    setLocalSubunitActivities(subunitActivitiesMap);
  }, [subunits, activities, unitId]);

  const handleDragStart = (event: any) => {
    const id = String(event.active.id);
    const found = (localSortedContents || []).find((i) => i.id === id);
    const isActivity = ['document', 'video', 'quiz', 'resource', 'assignment'].includes(
      (found as any)?.type,
    );
    setActiveUnitContentDragId(isActivity ? id : null);
  };

  const handleDragCancel = () => {
    setActiveUnitContentDragId(null);
  };

  // Handle drag end for unit content (subunits and unit activities)
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    // If dropped outside a valid target, just clear overlay and exit
    if (!over) {
      setActiveUnitContentDragId(null);
      return;
    }

    if (active.id !== over.id) {
      let newContents: Array<(SubunitData | ActivityData) & { id: string }> = [];
      let originalContents: Array<(SubunitData | ActivityData) & { id: string }> = [];

      setLocalSortedContents((prevContents) => {
        originalContents = [...prevContents];
        newContents = [...prevContents];
        const oldIndex = newContents.findIndex((item) => item.id === active.id);
        const newIndex = newContents.findIndex((item) => item.id === over.id);

        // Reorder the array
        const [movedItem] = newContents.splice(oldIndex, 1);
        newContents.splice(newIndex, 0, movedItem);

        return newContents;
      });

      // Send reorder request to backend
      try {
        // Create separate records for subunits and activities
        const subunitRecord: { [subunitId: string]: number } = {};
        const activityRecord: {
          quiz: Record<string, number>;
          document: Record<string, number>;
          video: Record<string, number>;
          resource: Record<string, number>;
          assignment: Record<string, number>;
        } = { quiz: {}, document: {}, video: {}, resource: {}, assignment: {} };

        newContents.forEach((item, index) => {
          const isActivity = ['document', 'video', 'quiz', 'resource', 'assignment'].includes(
            (item as any)?.type,
          );
          if (isActivity) {
            const type = (item as any).type as
              | 'document'
              | 'video'
              | 'quiz'
              | 'resource'
              | 'assignment';
            activityRecord[type][(item as any).id] = index;
          } else {
            subunitRecord[(item as any).id] = index;
          }
        });

        const requestResponse = await Request.post('course/reorder/subunits', {
          subunitRecord,
          activityRecord,
          unitId,
        });

        const response = await requestResponse?.json();

        if (!response || response.status !== 'success') {
          const errorMessage = response?.data || 'Unauthorized';
          console.error('Unit content reorder API Error:', errorMessage);
          toast.error(`Error reordering unit content: ${errorMessage}`);
          setLocalSortedContents(originalContents);
        } else {
          toast.success('Unit content reordered successfully');
          console.log('Unit content reordered successfully:', {
            itemId: active.id,
            subunitRecord,
            activityRecord,
          });

          const courseData = {
            id: response.data.courseId,
            name: response.data.courseStructure.name,
            units: response.data.courseStructure.units,
          };

          dispatch(setActiveCourseData(courseData));
        }
      } catch (error) {
        console.error('Unit content reorder request failed:', error);
        toast.error('Failed to reorder unit content. Please try again.');
        setLocalSortedContents(originalContents);
      }
    }
    setActiveUnitContentDragId(null);
  };

  // Handle drag end for subunit activities
  const handleSubunitActivityDragEnd = async (event: DragEndEvent, subunitId: string) => {
    const { active, over } = event;

    // If dropped outside a valid target, exit early
    if (!over) {
      return;
    }

    if (active.id !== over?.id) {
      let newActivities: ActivityData[] = [];
      let originalActivities: { [subunitId: string]: ActivityData[] } = {};

      setLocalSubunitActivities((prevActivities) => {
        originalActivities = { ...prevActivities };
        const currentActivities = prevActivities[subunitId] || [];
        newActivities = [...currentActivities];
        const oldIndex = newActivities.findIndex((activity) => activity.id === active.id);
        const newIndex = newActivities.findIndex((activity) => activity.id === over?.id);

        // Reorder the array
        const [movedActivity] = newActivities.splice(oldIndex, 1);
        newActivities.splice(newIndex, 0, movedActivity);

        return {
          ...prevActivities,
          [subunitId]: newActivities,
        };
      });

      // Send reorder request to backend
      try {
        const activityRecord: {
          quiz: Record<string, number>;
          document: Record<string, number>;
          video: Record<string, number>;
          resource: Record<string, number>;
          assignment: Record<string, number>;
        } = { quiz: {}, document: {}, video: {}, resource: {}, assignment: {} };

        newActivities.forEach((activity, index) => {
          activityRecord[activity.type][activity.id] = index;
        });

        const requestResponse = await Request.post('content/reorder', {
          activityRecord,
          subunitId,
        });

        const response = await requestResponse?.json();

        if (!response || response.status !== 'success') {
          const errorMessage = response?.data || 'Unauthorized';
          console.error('Subunit activity reorder API Error:', errorMessage);
          toast.error(`Error reordering subunit activities: ${errorMessage}`);
          setLocalSubunitActivities(originalActivities);
        } else {
          toast.success('Subunit activities reordered successfully');
          console.log('Subunit activity reordered successfully:', {
            subunitId,
            activityId: active.id,
            activityRecord,
          });

          const courseData = {
            id: response.data.courseId,
            name: response.data.courseStructure.name,
            units: response.data.courseStructure.units,
          };

          dispatch(setActiveCourseData(courseData));
        }
      } catch (error) {
        console.error('Subunit activity reorder request failed:', error);
        toast.error('Failed to reorder subunit activities. Please try again.');
        setLocalSubunitActivities(originalActivities);
      }
    }
  };

  return {
    localSortedContents,
    localSubunitActivities,
    activeUnitContentDragId,
    handleDragEnd,
    handleSubunitActivityDragEnd,
    handleDragStart,
    handleDragCancel,
  };
}
